import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";

const fetchDispatchersAssignments = async (
  token: string | undefined | null,
) => {
  const response = await fetch(`${apiVariable}/getDispatcherAssignments`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Network response error");
  }
  return response.json();
};

const buildDispatcherName = (
  nickname?: string | null,
  firstName?: string | null,
  lastName?: string | null,
) =>
  nickname?.trim() ||
  `${firstName ?? ""} ${lastName ?? ""}`.trim() ||
  "Unassigned";

const DispatchersPage = () => {
  const { token } = useAuth();
  const { data, error, isLoading } = useQuery({
    queryKey: ["dispatchersAssignments"],
    queryFn: () => fetchDispatchersAssignments(token),
  });

  const groupedDispatchers = useMemo(() => {
    if (!data) return [];
    return Object.values(
      data.reduce((acc: any, cur: any) => {
        const dispatcherName = buildDispatcherName(
          cur.dispatcherNickname,
          cur.dispatcherFirstName,
          cur.dispatcherLastName,
        );

        if (!acc[cur.dispatcherId]) {
          acc[cur.dispatcherId] = {
            dispatcherId: cur.dispatcherId,
            dispatcherName,
            drivers: [],
          };
        }
        acc[cur.dispatcherId].drivers.push({
          driver: cur.driver,
          truck: cur.truck,
          trailer: cur.trailer,
        });
        return acc;
      }, {}),
    );
  }, [data]);

  if (isLoading) {
    return (
      <Box
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 4 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error instanceof Error) {
    return <Alert severity="error">Error: {error.message}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {groupedDispatchers.map((dispatcher: any) => (
        <Accordion key={dispatcher.dispatcherId} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{dispatcher.dispatcherName}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {dispatcher.drivers && dispatcher.drivers.length > 0 ? (
              <List>
                {dispatcher.drivers.map((assignment: any, index: number) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={`${assignment.driver.firstName} ${assignment.driver.lastName} | Phone: ${assignment.driver.phoneNumber}`}
                      secondary={
                        <>
                          {assignment.truck
                            ? `Truck: Unit number: ${assignment.truck.unitNumber} | VIN: ${assignment.truck.vin} | License Plate: ${assignment.truck.licensePlate}`
                            : "Truck: None"}{" "}
                          <br />
                          {assignment.trailer
                            ? `Trailer: ${assignment.trailer.unitNumber}`
                            : "Trailer: None"}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2">No drivers assigned.</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default DispatchersPage;
